import { Box, Menu, MenuItem, ListItemIcon, ListItemText, Typography } from '@mui/material'
import { Download, Place } from '@mui/icons-material'
import { useState } from 'react'
import { ConvertTime } from '@/components/smartUtils/conversion/ConvertTime'
import { preview } from '@/components/form/preview'
import { useEnvironmentURLs } from '@/atoms'
import { togglePoDMarker, fitMap, useActivePodMarker } from '@/map'
import { FilePreview } from '@workwave-tidal/lab'
import { ActionBarItem } from '@workwave-tidal/core/components/ActionBar'
import { MoreVert } from '@workwave-tidal/core/icons'

import { useTexts } from '../useTexts'

interface Props {
  extOrder: uui.domain.client.rm.ExtendedOrderStep
}

export function Pictures(props: Props) {
  const { extOrder } = props
  const texts = useTexts()
  const { podsBaseUrl } = useEnvironmentURLs()

  const pictures = extOrder.orderStep.trackingData?.pods?.pictures
  const picturesList = pictures ? Object.values(pictures).sort((a, b) => a.sec - b.sec) : []

  if (picturesList.length === 0) {
    return (
      <preview.Field label={texts.pictures.label} testId="execution-pictures">
        <preview.NullValue />
      </preview.Field>
    )
  }

  return (
    <Box data-testid="execution-pictures" sx={{ width: '100%' }}>
      <preview.Label>{texts.pictures.label}</preview.Label>
      {picturesList.map((picture, idx) => {
        const { sec, text, uuid, latLng } = picture
        const time = <ConvertTime seconds={sec} showSeconds={false} />
        const src = picture.podType === 'pictures' ? `${podsBaseUrl}${picture.token}` : ''

        return (
          <PictureItem
            key={uuid || `picture_${idx}`}
            src={src}
            index={idx}
            time={time}
            text={text}
            latLng={latLng}
            uuid={uuid}
          />
        )
      })}
    </Box>
  )
}

function PictureItem({
  src,
  index,
  time,
  text,
  latLng,
  uuid,
}: {
  src: string
  index: number
  time: React.ReactNode
  text?: string
  latLng?: { lat: number; lng: number } | null
  uuid?: string
}) {
  const texts = useTexts()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const activePodMarker = useActivePodMarker()
  const isOnMap = !!uuid && activePodMarker?.uid === uuid

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleShowOnMap = () => {
    if (latLng && uuid) {
      togglePoDMarker(uuid, latLng)

      // Only fit when revealing the marker; toggling it off shouldn't recenter the map.
      if (!isOnMap) {
        fitMap([latLng], { minResolution: 3, preventIfVisible: true }, 'clear')
      }
    }
    handleMenuClose()
  }

  const handleDownload = () => {
    if (!src) return

    const link = document.createElement('a')
    link.href = src
    link.target = '_blank'
    link.rel = 'noopener noreferrer'
    link.download = `picture_${index + 1}.jpg`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    handleMenuClose()
  }

  return (
    <Box sx={{ mb: 2, width: '100%' }}>
      <Box
        sx={{
          width: '100%',
          maxWidth: '100%',
          '& > *': {
            width: '100% !important',
            maxWidth: '100% !important',
          },
        }}
      >
        <FilePreview
          variant="image"
          size="large"
          src={src}
          alt={`${texts.picture.label} ${index + 1}`}
          tooltip={`${texts.picture.label} ${index + 1}`}
          actions={[
            <ActionBarItem
              key="menu"
              icon={<MoreVert />}
              onClick={handleMenuClick}
              label=""
              testId="picture-menu"
            />,
          ]}
        />
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleShowOnMap} disabled={!latLng}>
          <ListItemIcon>
            <Place fontSize="small" />
          </ListItemIcon>
          <ListItemText>{isOnMap ? texts.hideOnMap.label : texts.showOnMap.label}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDownload}>
          <ListItemIcon>
            <Download fontSize="small" />
          </ListItemIcon>
          <ListItemText>{texts.downLoadImage.label}</ListItemText>
        </MenuItem>
      </Menu>
      <Box
        sx={{
          mt: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: 'text.secondary',
          fontSize: '0.875rem',
        }}
      >
        <Typography variant="caption">
          {texts.picture.label} {index + 1} • {time}
        </Typography>
        {text && <Typography variant="caption">{text}</Typography>}
      </Box>
    </Box>
  )
}
